import { useEffect, useRef } from 'react'

/**
 * Tournament Stadium — champion-reveal spark-burst.
 *
 * Custom canvas, no library. Single radial explosion of bright lines
 * radiating from the centre, with a brief full-screen brightness
 * flash on frame 0. The "bang" effect — closer to a Smash Bros final-
 * hit or Fortnite Victory Royale moment than a confetti shower.
 *
 * Choreography (per the Emil framework — peak celebratory moment):
 *   0-50ms    : white flash overlay at 35% opacity
 *   0-700ms   : 64 spark lines accelerate outward from centre
 *   400-1100ms: lines fade to 0
 *   total run : ~1100ms, then onDone fires
 *
 * The line easing is `--ease-out-strong` baked into the velocity
 * curve so motion is fast at the start (the impact) and slows toward
 * the screen edges (the dissipation).
 *
 * Hardware-accelerated path: a single 2D canvas painted via
 * requestAnimationFrame. Sparks are line strokes (cheap) not particle
 * objects (would require per-frame allocation). The full-screen flash
 * is a CSS layer with `opacity` transition — off main thread.
 *
 * `prefers-reduced-motion: reduce` suppresses the whole effect — no
 * point firing a flash on a TV that an attendee with vestibular
 * sensitivity might be near. The CompletePhase still renders the
 * podium reveal; just without the bang.
 *
 * Props:
 *   - active: render the canvas only when truly mounted to spark. The
 *     component remounts (key change) to re-fire; parent owns the
 *     lifecycle.
 *   - onDone: optional — fired ~1100ms after mount.
 */
export function SparkBurst({
  active = true,
  onDone,
}: {
  active?: boolean
  onDone?: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const flashRef = useRef<HTMLDivElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const startRef = useRef<number | null>(null)

  useEffect(() => {
    if (!active) return
    if (typeof window === 'undefined') return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      // Skip motion; still fire onDone so the parent's state machine
      // proceeds (e.g. clears the spark trigger).
      const t = setTimeout(() => onDone?.(), 200)
      return () => clearTimeout(t)
    }

    const canvas = canvasRef.current
    const flash = flashRef.current
    if (!canvas) return

    // Size canvas to the viewport at device pixel ratio for crisp lines
    // on 4K TVs. Window resize during the burst is rare so we size
    // once at mount.
    const dpr = Math.max(1, Math.min(2.5, window.devicePixelRatio || 1))
    const w = window.innerWidth
    const h = window.innerHeight
    canvas.width = Math.floor(w * dpr)
    canvas.height = Math.floor(h * dpr)
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)

    const cx = w / 2
    const cy = h / 2
    // Sparks: 64 evenly-distributed radial lines with small random
    // velocity + length jitter so the burst doesn't look mechanical.
    const SPARK_COUNT = 64
    const sparks = Array.from({ length: SPARK_COUNT }, (_, i) => {
      const baseAngle = (i / SPARK_COUNT) * Math.PI * 2
      const jitterAngle = baseAngle + (deterministicRand(i) - 0.5) * 0.06
      // Reach: 60-95% of half-viewport diagonal. Mix so the silhouette
      // is varied (some sparks go further than others).
      const reachFrac = 0.6 + deterministicRand(i + 100) * 0.35
      const reach = Math.hypot(cx, cy) * reachFrac
      // Two colour families — primary blue + secondary purple — so the
      // burst feels brand-aligned not generic.
      const color = i % 2 === 0 ? '#3D6FFF' : '#D946EF'
      return { angle: jitterAngle, reach, color }
    })

    const TOTAL_MS = 1100
    const SPARK_MS = 700        // outward motion duration
    const FADE_START = 400      // when sparks start fading
    const FADE_END = 1100

    // Trigger the flash overlay — pure CSS transition handles the
    // opacity ramp so it's off main thread.
    if (flash) {
      flash.style.opacity = '0.35'
      requestAnimationFrame(() => {
        if (flash) flash.style.opacity = '0'
      })
    }

    const tick = (ts: number) => {
      if (startRef.current == null) startRef.current = ts
      const elapsed = ts - startRef.current

      // Clear with a slight residual so the trail tapers naturally
      // rather than blinking off each frame.
      ctx.clearRect(0, 0, w, h)

      // Eased outward progress. Strong ease-out so sparks shoot fast
      // initially then decelerate at the edges.
      const tNorm = Math.min(1, elapsed / SPARK_MS)
      const eased = 1 - Math.pow(1 - tNorm, 3.2)

      // Fade ramp from 1 → 0 over [FADE_START, FADE_END].
      const fade =
        elapsed < FADE_START
          ? 1
          : Math.max(0, 1 - (elapsed - FADE_START) / (FADE_END - FADE_START))

      for (const s of sparks) {
        const dist = s.reach * eased
        const x1 = cx + Math.cos(s.angle) * dist * 0.25 // tail start
        const y1 = cy + Math.sin(s.angle) * dist * 0.25
        const x2 = cx + Math.cos(s.angle) * dist        // head
        const y2 = cy + Math.sin(s.angle) * dist
        const grad = ctx.createLinearGradient(x1, y1, x2, y2)
        // Tail: brand colour, head: white hot. Fade applied via global
        // alpha so each line scales together.
        grad.addColorStop(0, withAlpha(s.color, 0))
        grad.addColorStop(0.4, withAlpha(s.color, 0.9 * fade))
        grad.addColorStop(1, withAlpha('#FFFFFF', fade))
        ctx.strokeStyle = grad
        ctx.lineWidth = 3
        ctx.lineCap = 'round'
        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.stroke()
      }

      if (elapsed < TOTAL_MS) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        ctx.clearRect(0, 0, w, h)
        onDone?.()
      }
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      startRef.current = null
    }
  }, [active, onDone])

  if (!active) return null

  // `absolute` (NOT `fixed`) + low z-index — the burst lives INSIDE
  // the CompletePhase root and the parent stacking context puts the
  // podium pillars + hero blocks above it (their `relative` + higher
  // z-index on the content row wins). The result: rays radiate from
  // behind the champion, framing them rather than obscuring them.
  return (
    <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
      {/* Brightness flash — CSS opacity transition for off-main-thread. */}
      <div
        ref={flashRef}
        className="absolute inset-0"
        style={{
          backgroundColor: '#FFFFFF',
          opacity: 0,
          transition: 'opacity 220ms cubic-bezier(0.23, 1, 0.32, 1)',
          mixBlendMode: 'screen',
        }}
      />
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  )
}

/**
 * Deterministic pseudo-random in [0, 1) from an integer seed. Avoids
 * `Math.random()` so the burst is identical run-to-run — useful when
 * UAT'ing and reviewing frame-by-frame, per Emil's "review next day"
 * principle.
 */
function deterministicRand(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280
  return x - Math.floor(x)
}

function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha))})`
}
